// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc */

type APIResolver = () => Promise<*>;

/** The will return 400 if you pass more than this many requests */
export const MAX_REQUESTS_PER_BATCH = 50;

// Divide pending service calls into batches

const generateRequestBatches = (
  calls: { [string]: string },
  deps: { [string]: string },
): Array<{ [string]: Object }> => {
  let keys = Object.keys(calls);

  if (keys.length > MAX_REQUESTS_PER_BATCH) {
    // we're going to need to break into multiple batches

    // Sort keys first so that calls to the same endpoint are likely to be together
    keys.sort();

    // dependencies are parent links in a tree.  Build the tree...
    // this will map call ID -> dependant list.  "" is the root

    const deptree: { [string]: Array<string> } = {};
    keys.forEach(k => {
      const dep = deps[k] || '';
      const list = deptree[dep] || [];
      list.push(k);
      deptree[dep] = list;
    });

    // rebuild the key list with a preorder traversal
    keys = [];
    const addNode = key => {
      keys.push(key);
      const list = deptree[key];
      if (list && list.length > 0) {
        list.forEach(addNode);
      }
    };
    addNode('');
    keys.shift(); // remove ""
  }

  // break requests into batches.  When we make a new batch, we need to insert the
  // dependency chain for the first item.
  let request = {};
  let count = 0;
  const batches = [request];
  keys.forEach(key => {
    if (count >= MAX_REQUESTS_PER_BATCH) {
      request = {};
      batches.push(request);
      count = 0;
      for (let dep = deps[key]; dep; dep = deps[dep]) {
        request[dep] = { url: calls[dep] };
        count += 1;
      }
    }
    request[key] = { url: calls[key] };
    count += 1;
  });
  return batches;
};

// resolve a single batch request

const resolveRequest = request =>
  fetch('/data/1.0/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    credentials: 'same-origin',
  }).then(res => res.json());

// resolve multiple batch requests and merge results

const resolveBatches = batches => {
  if (!(batches && batches.length > 0)) {
    return Promise.resolve({});
  }
  if (batches.length === 1) {
    return resolveRequest(batches[0]);
  }
  return Promise.all(batches.map(resolveRequest)).then(results =>
    Object.assign({}, ...results.filter(x => !!x)),
  );
};

const DEPENDENCY_PATTERN = /\{(\w+):[^}]*\}/;

class APICaller {
  stagedCalls: { [string]: string } = {};

  // Each call may have at most one dependnecy.  This maps call ID -> dependency ID
  dependencies: { [string]: string } = {};

  stager = (endpoint: string) => {
    const id = this.getId(endpoint);
    this.stagedCalls[id] = endpoint;
    const depMatch = endpoint.match(DEPENDENCY_PATTERN);
    if (depMatch && depMatch.length >= 1) {
      // extract the captured ID
      // eslint-disable-next-line prefer-destructuring
      this.dependencies[id] = depMatch[1];
    } else {
      delete this.dependencies[id]; // in case we overwrote an endpoint
    }
    return id;
  };

  getId = (endpoint: string) => endpoint.replace(/[\W]/g, '_');

  hasStagedCalls = () => Object.keys(this.stagedCalls).length !== 0;

  filterStagedCalls = (predicate: (key: string, endpoint: string) => boolean): boolean => {
    let keepAny = false;
    const keepSet: { [string]: ?boolean } = {};
    const allKeys = Object.keys(this.stagedCalls);

    // stuff the caller specifically wants to keep
    allKeys.forEach(k => {
      if (predicate(k, this.stagedCalls[k])) {
        keepAny = true;
        keepSet[k] = true;
        for (let dep = this.dependencies[k]; dep && !keepSet[dep]; dep = this.dependencies[dep]) {
          keepSet[dep] = true;
        }
      }
    });
    allKeys.forEach(k => {
      if (!keepSet[k]) {
        delete this.stagedCalls[k];
        delete this.dependencies[k];
      }
    });
    return keepAny;
  };

  resolve: APIResolver = () =>
    resolveBatches(generateRequestBatches(this.stagedCalls, this.dependencies));
}

export default APICaller;
